"use client";

import Image from "next/image";
import type { TestimonialsSectionQueryResult } from "../../sanity/schemas/lib/queries";
import { TESTIMONIALS_MAX_PERSPECTIVES } from "../../constants/testimonials-section";
import { useState } from "react";
import { PerspectiveCard } from "../ui/PerspectiveCard";

interface TestimonialsSectionProps {
    sanityContent: TestimonialsSectionQueryResult;
}

export function TestimonialsSection({ sanityContent }: TestimonialsSectionProps) {
    const visiblePerspectives = sanityContent.perspectives.slice(0, TESTIMONIALS_MAX_PERSPECTIVES);
    const hasSlider = visiblePerspectives.length > 1;
    const [activeIndex, setActiveIndex] = useState(0);
    const activeSlideIndex = Math.min(activeIndex, visiblePerspectives.length - 1);
    const canGoPrevious = activeSlideIndex > 0;
    const canGoNext = activeSlideIndex < visiblePerspectives.length - 1;

    function goToPrevious() {
        setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
    }

    function goToNext() {
        setActiveIndex((currentIndex) =>
            Math.min(currentIndex + 1, visiblePerspectives.length - 1),
        );
    }

    return (
        <section className="pb-14 md:py-39.5 bg-ink relative text-white">
            <div className="mx-auto max-w-7xl px-5 md:px-20 relative z-10 pt-20">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center pb-6 mb-6 gap-6 md:gap-0  border-b border-white/10">
                    <h2 className="text-big font-semibold leading-120 md:text-h2-desktop  ">
                        {sanityContent.title}
                    </h2>

                    {hasSlider ? (
                        <div className="flex gap-2  ">
                            <button
                                type="button"
                                disabled={!canGoPrevious}
                                className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 transition-colors hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-white/5"
                                aria-label={sanityContent.controls.previous}
                                onClick={goToPrevious}
                            >
                                <Image
                                    src="/arrow-left.png"
                                    alt="arrow-right"
                                    width={16}
                                    height={16}
                                    className="rotate-180 w-4 h-4 shrink-0"
                                />
                            </button>
                            <button
                                type="button"
                                disabled={!canGoNext}
                                className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 transition-colors hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-white/5"
                                aria-label={sanityContent.controls.next}
                                onClick={goToNext}
                            >
                                <Image
                                    src="/arrow-left.png"
                                    alt="arrow-left"
                                    width={16}
                                    height={16}
                                    className="w-4 h-4 shrink-0"
                                />
                            </button>
                        </div>
                    ) : null}
                </div>

                {hasSlider ? (
                    <div className="overflow-hidden" aria-live="polite">
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeSlideIndex * 100}%)` }}
                        >
                            {visiblePerspectives.map((perspective, index) => (
                                <div
                                    className="w-full shrink-0"
                                    key={`${perspective.person.name}-${index}`}
                                >
                                    <PerspectiveCard perspective={perspective} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <PerspectiveCard perspective={visiblePerspectives[0]} />
                )}
            </div>
        </section>
    );
}
